import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Mail, Phone, Building2, Inbox, CalendarDays, Search } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sv_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");

  const fetchLeads = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(""); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) fetchLeads(token); }, [token, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Login failed");
    }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setLeads([]); };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center px-6" data-theme="d">
        <div className="pointer-events-none fixed inset-0 opacity-40" style={{ background: "radial-gradient(60% 50% at 50% 0%, var(--glow-color), transparent 70%)" }} />
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative w-full max-w-sm rounded-3xl glass p-8"
          data-testid="admin-login-form"
        >
          <Logo className="h-7 mb-6" />
          <h1 className="font-display text-2xl font-bold">Leads Dashboard</h1>
          <p className="font-body text-sm text-[var(--text-secondary)] mb-6">Private access · admin only</p>
          <input data-testid="admin-email" type="email" required placeholder="Email" className="field mb-3"
                 value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="admin-password" type="password" required placeholder="Password" className="field mb-4"
                 value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {error && <p data-testid="admin-login-error" className="text-sm text-red-400 mb-3">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" className="btn-accent w-full rounded-full py-3.5 font-heading font-semibold">Sign in</button>
        </motion.form>
      </div>
    );
  }

  const filtered = leads
    .filter((l) => filter === "all" || l.kind === filter)
    .filter((l) => !q || `${l.name} ${l.email} ${l.company || ""} ${l.message}`.toLowerCase().includes(q.toLowerCase()));

  const counts = {
    all: leads.length,
    enquiry: leads.filter((l) => l.kind === "enquiry").length,
    booking: leads.filter((l) => l.kind === "booking").length,
  };

  return (
    <div className="min-h-screen" data-theme="d">
      <header className="sticky top-0 z-20 glass">
        <div className="mx-auto max-w-[1200px] px-6 py-4 flex items-center justify-between">
          <Logo className="h-6" />
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={() => fetchLeads(token)} className="grid h-10 w-10 place-items-center rounded-full border border-[var(--border-subtle)] hover:border-[var(--border-hover)]">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] px-4 py-2 font-heading text-sm hover:border-[var(--border-hover)]">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold mb-8">Leads <span className="text-gradient">Dashboard</span></h1>

        <div className="grid grid-cols-3 gap-4 mb-8">
          {[["Total", counts.all, Inbox], ["Enquiries", counts.enquiry, Mail], ["Bookings", counts.booking, CalendarDays]].map(([label, val, Icon]) => (
            <div key={label} className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-5">
              <Icon className="text-[var(--accent-primary)] mb-3" size={20} />
              <div className="font-display text-3xl font-bold">{val}</div>
              <div className="font-mono text-xs uppercase tracking-wider text-[var(--text-muted)]">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          {["all", "enquiry", "booking"].map((f) => (
            <button key={f} data-testid={`admin-filter-${f}`} onClick={() => setFilter(f)}
              className={`rounded-full px-5 py-2 font-heading text-sm capitalize transition-all ${filter === f ? "btn-accent" : "border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-hover)]"}`}>
              {f === "all" ? "All" : f + "s"}
            </button>
          ))}
          <div className="relative ml-auto">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="field pl-9 py-2 w-56" />
          </div>
        </div>

        {loading ? (
          <p className="font-body text-[var(--text-secondary)]">Loading…</p>
        ) : filtered.length === 0 ? (
          <p data-testid="admin-empty" className="font-body text-[var(--text-secondary)]">No leads yet.</p>
        ) : (
          <div className="grid gap-4" data-testid="admin-leads-list">
            {filtered.map((l) => (
              <motion.div key={l.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-lg font-semibold break-words">{l.name}</h3>
                      <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${l.kind === "booking" ? "btn-accent" : "border border-[var(--border-subtle)] text-[var(--text-secondary)]"}`}>{l.kind}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-body text-sm text-[var(--text-secondary)]">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 break-all hover:text-[var(--text-primary)]"><Mail size={13} /> {l.email}</a>
                      {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 hover:text-[var(--text-primary)]"><Phone size={13} /> {l.phone}</a>}
                      {l.company && <span className="inline-flex items-center gap-1.5 break-words"><Building2 size={13} /> {l.company}</span>}
                    </div>
                  </div>
                  <span className="font-mono text-xs text-[var(--text-muted)] shrink-0">{new Date(l.created_at).toLocaleString()}</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {l.service && <span className="rounded-full border border-[var(--border-subtle)] px-3 py-1 font-mono text-[11px] text-[var(--text-secondary)]">{l.service}</span>}
                  {l.budget && <span className="rounded-full border border-[var(--border-subtle)] px-3 py-1 font-mono text-[11px] text-[var(--accent-primary)]">{l.budget}</span>}
                </div>
                <p className="font-body text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap">{l.message}</p>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
